import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createAccountSession } from '../../Account/AccountSession';
import type { AccountSessionResult } from '../../Account/Type/AccountTypes';
import { HS_BASE_URL } from '../../Utils/SessionUrlUtils';
import { getCurrentLanguage, t } from '../../i18n';

type AccountMessageType = 'hs.account.loaded' | 'hs.account.avatarUpdated';

type AccountMessage = {
    type: AccountMessageType;
    userId?: string;
    avatarUrl?: string;
};

interface AccountProfileModalProps {
    open: boolean;
    onClose: () => void;
    onAvatarUpdated?: (data: { userId?: string; avatarUrl: string }) => void;
}

const ACCOUNT_MESSAGE_TYPES = new Set<AccountMessageType>([
    'hs.account.loaded',
    'hs.account.avatarUpdated',
]);

const parseAccountMessage = (data: unknown): AccountMessage | null => {
    const parsedData = typeof data === 'string'
        ? (() => {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        })()
        : data;

    if (!parsedData || typeof parsedData !== 'object') return null;

    const type = (parsedData as { type?: unknown }).type;
    if (typeof type !== 'string') return null;
    if (!ACCOUNT_MESSAGE_TYPES.has(type as AccountMessageType)) return null;

    const userId = (parsedData as { userId?: unknown }).userId;
    const avatarUrl = (parsedData as { avatarUrl?: unknown }).avatarUrl;

    return {
        type: type as AccountMessageType,
        userId: typeof userId === 'string' && userId.length > 0 ? userId : undefined,
        avatarUrl: typeof avatarUrl === 'string' && avatarUrl.length > 0 ? avatarUrl : undefined,
    };
};

const addOriginVariants = (origins: Set<string>, rawUrl?: string | null): void => {
    if (!rawUrl) return;

    try {
        const url = new URL(rawUrl);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return;

        origins.add(url.origin);

        if (url.hostname.startsWith('www.')) {
            const withoutWww = new URL(url.toString());
            withoutWww.hostname = url.hostname.replace(/^www\./, '');
            origins.add(withoutWww.origin);
            return;
        }

        const withWww = new URL(url.toString());
        withWww.hostname = `www.${url.hostname}`;
        origins.add(withWww.origin);
    } catch {}
};

const isTrustedPokerSolutionsOrigin = (origin: string): boolean => {
    try {
        const url = new URL(origin);
        const hostname = url.hostname.toLowerCase();
        return hostname === 'pokersolutions.com' || hostname.endsWith('.pokersolutions.com');
    } catch {
        return false;
    }
};

const AccountProfileModal: React.FC<AccountProfileModalProps> = ({ open, onClose, onAvatarUpdated }) => {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [session, setSession] = useState<AccountSessionResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loaded, setLoaded] = useState(false);
    const accountUrl = useMemo(() => session?.accountUrl?.trim() || '', [session?.accountUrl]);
    const trustedOrigins = useMemo(() => {
        const origins = new Set<string>();
        addOriginVariants(origins, accountUrl);
        addOriginVariants(origins, HS_BASE_URL);
        return origins;
    }, [accountUrl]);

    useEffect(() => {
        if (!open) {
            setSession(null);
            setLoading(false);
            setError(null);
            setLoaded(false);
            return;
        }

        const controller = new AbortController();

        setLoaded(false);
        setLoading(true);
        setError(null);
        setSession(null);

        createAccountSession({
            input: { lang: getCurrentLanguage() },
            signal: controller.signal,
        })
            .then((nextSession) => {
                if (controller.signal.aborted) return;
                setSession(nextSession);
                if (!nextSession.accountUrl) {
                    setError('Account profile is unavailable.');
                }
            })
            .catch((err) => {
                if (controller.signal.aborted) return;
                setError(err instanceof Error && err.message ? err.message : 'Account profile is unavailable.');
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [open]);

    useEffect(() => {
        if (!open || trustedOrigins.size === 0) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) return;
            if (!trustedOrigins.has(event.origin) && !isTrustedPokerSolutionsOrigin(event.origin)) return;

            const message = parseAccountMessage(event.data);
            if (!message) return;

            if (message.type === 'hs.account.loaded') {
                setLoaded(true);
                return;
            }

            if (message.type === 'hs.account.avatarUpdated' && message.avatarUrl) {
                setLoaded(true);
                onAvatarUpdated?.({
                    userId: message.userId,
                    avatarUrl: message.avatarUrl,
                });
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [onAvatarUpdated, open, trustedOrigins]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            sx={{
                zIndex: 20000,
                '& .MuiDialog-paper': {
                    width: 'min(960px, calc(100vw - 32px))',
                    height: 'min(860px, calc(100vh - 32px))',
                    m: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#191A1F',
                    color: 'white',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.12)',
                    overflow: 'hidden',
                },
                '& .MuiDialogContent-root': {
                    p: 0,
                    overflow: 'hidden',
                    flex: '1 1 auto',
                    minHeight: 0,
                    width: '100%',
                    height: '100%',
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    flex: '0 0 auto',
                    minHeight: 48,
                    px: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.10)',
                }}
            >
                <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                    {t('DesktopHeader:menu.myAccount')}
                </Typography>
                <IconButton
                    aria-label={t('common:actions.close')}
                    onClick={onClose}
                    sx={{ color: 'rgba(255,255,255,0.72)' }}
                >
                    <CloseIcon />
                </IconButton>
            </Stack>
            <DialogContent>
                <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: '#1c1e25' }}>
                    {error ? (
                        <Stack sx={{ width: '100%', height: '100%', p: 3 }} alignItems="center" justifyContent="center">
                            <Alert severity="error">{error}</Alert>
                        </Stack>
                    ) : accountUrl ? (
                        <iframe
                            ref={iframeRef}
                            title={t('DesktopHeader:menu.myAccount')}
                            src={accountUrl}
                            style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
                            allow="clipboard-read; clipboard-write; fullscreen"
                        />
                    ) : null}
                    {!error && (loading || (accountUrl && !loaded)) && (
                        <Stack
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: '#191A1F',
                                pointerEvents: 'auto',
                            }}
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                        >
                            <CircularProgress size={26} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
                                Loading account...
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AccountProfileModal;
